import React, { useState } from 'react';
import { Box, Tabs, Tab, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

/**
 * Calcula o IP do peer dentro de uma /30 ou /31.
 * Ex: peerIp("10.0.0.1/30") → "10.0.0.2"
 */
export function peerIp(ipInterface) {
    if (!ipInterface) return null;
    const m = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/.exec(ipInterface.trim());
    if (!m) return null;
    const o = m.slice(1, 5).map(Number);
    if (o.some(n => n < 0 || n > 255)) return null;
    const plen = +m[5];
    const ipNum = ((o[0] << 24) | (o[1] << 16) | (o[2] << 8) | o[3]) >>> 0;

    let peer;
    if (plen === 30) {
        const network = (ipNum & 0xfffffffc) >>> 0;
        const h1 = (network | 0x01) >>> 0;
        const h2 = (network | 0x02) >>> 0;
        if (ipNum !== h1 && ipNum !== h2) return null;
        peer = ipNum === h1 ? h2 : h1;
    } else if (plen === 31) {
        peer = (ipNum ^ 0x01) >>> 0;
    } else {
        return null;
    }
    return [(peer >>> 24) & 0xff, (peer >>> 16) & 0xff, (peer >>> 8) & 0xff, peer & 0xff].join('.');
}

const VENDORS = [
    {
        id: 'mikrotik', label: 'Mikrotik (RouterOS)',
        build: ({ source, destination, peer, name }) =>
            `/interface gre add name=${name || 'gre-anubis'} remote-address=${source} local-address=${destination} keepalive=00:00:00,0
/ip address add address=${peer}/30 interface=${name || 'gre-anubis'}`,
    },
    {
        id: 'huawei', label: 'Huawei (NE/AR — VRP)',
        build: ({ source, destination, peer, name }) =>
            `interface Tunnel0/0/0
 description ${name || 'Anubis-link'}
 ip address ${peer} 255.255.255.252
 tunnel-protocol gre
 source ${destination}
 destination ${source}
 mtu 1476
quit`,
    },
    {
        id: 'cisco', label: 'Cisco (IOS / IOS-XE)',
        build: ({ source, destination, peer, name }) =>
            `interface Tunnel0
 description ${name || 'Anubis-link'}
 ip address ${peer} 255.255.255.252
 tunnel source ${destination}
 tunnel destination ${source}
 ip mtu 1476
 keepalive 0
exit`,
    },
    {
        id: 'juniper', label: 'Juniper (Junos)',
        build: ({ source, destination, peer, name }) =>
            `set interfaces gr-0/0/0 unit 0 description ${name || 'Anubis-link'}
set interfaces gr-0/0/0 unit 0 tunnel source ${destination}
set interfaces gr-0/0/0 unit 0 tunnel destination ${source}
set interfaces gr-0/0/0 unit 0 family inet address ${peer}/30
set interfaces gr-0/0/0 unit 0 family inet mtu 1476`,
    },
];

export default function PeerScriptPanel({ source, destination, ipInterface, name }) {
    const [tab, setTab] = useState(0);
    const [copied, setCopied] = useState(false);

    const peer = peerIp(ipInterface);
    const ready = source && destination && peer;

    const onCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (_) { /* noop */ }
    };

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Script para o peer (lado do cliente)</Typography>
                {ready
                    ? <Chip size="small" color="success" label={`peer ${peer}`} />
                    : <Chip size="small" variant="outlined" label="preencha src/dst/ip" />}
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ minHeight: 32, mb: 1 }}>
                {VENDORS.map((v, i) => (
                    <Tab key={v.id} label={v.label} value={i} sx={{ minHeight: 32, py: 0.5, fontSize: 12 }} />
                ))}
            </Tabs>

            {VENDORS.map((v, i) => {
                if (i !== tab) return null;
                const script = ready ? v.build({ source, destination, peer, name }) : '';
                return (
                    <Box key={v.id} sx={{ position: 'relative', flex: 1 }}>
                        <Box component="pre" sx={{
                            fontFamily: 'monospace', fontSize: 12, m: 0, p: 1.5,
                            bgcolor: '#1e1e1e', color: '#dcdcdc', borderRadius: 1,
                            whiteSpace: 'pre-wrap', minHeight: 180, overflow: 'auto',
                        }}>
                            {ready
                                ? script
                                : <span style={{ color: '#888', fontStyle: 'italic' }}>
                                      preencha Source, Destination e IP /30 pra gerar o script…
                                  </span>}
                        </Box>
                        {ready && (
                            <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                                <IconButton
                                    size="small"
                                    onClick={() => onCopy(script)}
                                    sx={{ position: 'absolute', top: 4, right: 4, color: copied ? 'success.main' : '#aaa' }}
                                >
                                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            })}

            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                Aviso: Mikrotik/Cisco com keepalive desabilitado (Linux não responde). MTU sugerido 1476.
            </Typography>
        </Box>
    );
}
